'use client';

import { useRef, useState, useEffect } from 'react';
import useHorizontalScrollObserver from '@app/lib/hooks/use-horizontal-scroll-observer';
import cn from 'clsx';
import s from './product-assistance-drawer-header.module.css';
import { DrawerCloseButton, Side } from '@app/components/ui/drawer/Drawer';
import { type Labels } from './product-assistance-drawer';

interface ProductAssistanceDrawerHeaderProps {
    navItems: Labels[];
    onClick: (item: string) => void;
    activeItem: string;
    drawerDirection: Side;
}

const ProductAssistanceDrawerHeader = ({
    navItems,
    onClick,
    activeItem,
    drawerDirection,
}: ProductAssistanceDrawerHeaderProps) => {
    const { scrollContainerRef, hasReachedEnd, hasOverflowRight } =
        useHorizontalScrollObserver();
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
    const [underlineStyle, setUnderlineStyle] = useState({});
    itemRefs.current = navItems.map((_, i) => itemRefs.current[i] || null);

    const updateUnderlinePosition = (index: number) => {
        const ref = itemRefs.current[index];
        if (ref) {
            const { offsetLeft, offsetWidth } = ref;
            setUnderlineStyle({ left: offsetLeft, width: offsetWidth });
        }
    };

    const scrollToActiveItem = (activeIndex: number) => {
        const activeNavItem = itemRefs.current[activeIndex];
        if (activeNavItem && activeIndex > 1) {
            activeNavItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    };

    useEffect(() => {
        const activeIndex = navItems.findIndex(
            (item) => item.id === activeItem
        );
        updateUnderlinePosition(activeIndex);
        scrollToActiveItem(activeIndex);
    }, [activeItem, navItems]);

    const handleClick = (item: Labels, index: number): void => {
        onClick(item.id);
        updateUnderlinePosition(index);
        scrollToActiveItem(index);
    };

    // Apply gradient mask effect when scroll container has overflow
    // and the end of the scroll container has not been reached.
    const gradientMask =
        hasOverflowRight && !hasReachedEnd
            ? cn(s.gradientMask, s.active)
            : s.gradientMask;

    const buttonVariant = drawerDirection === 'bottom' ? 'text' : 'secondary';

    return (
        <div className={cn(s.root)}>
            <div
                className={cn(s.scrollContainer, s.spacer)}
                ref={scrollContainerRef}
            >
                <ul role="list" className={s.cluster}>
                    {navItems.map((navItem, index) => (
                        <li
                            key={navItem.id}
                            className={cn(s.tab, 'mmds-component-one')}
                            ref={(el) => {itemRefs.current[index] = el}}
                            onClick={() => handleClick(navItem, index)}
                        >
                            <button>{navItem.label}</button>
                        </li>
                    ))}
                </ul>
                <div className={s.activeUnderline} style={underlineStyle} />
            </div>
            <div className={cn(gradientMask)}>
                <DrawerCloseButton closeButtonVariant={buttonVariant} />
            </div>
        </div>
    );
};

export default ProductAssistanceDrawerHeader;
